/**
 * Retail › Tools › Send me a test Teams card (admin 0.54.0, pcb-retail-api 3.41.0; Bill 2026-11-03).
 * 0.55.0 / pcb-retail-api 3.42.0 ("Does not test a settle"): the card now carries the real Approve / Reject buttons.
 *
 *   POST v3/admin/tools/bot-test-card   → { code: SENT | FAILED, testId, sentTo, message, detail }
 *                                         409 NOT_INSTALLED (nothing sent) · 429 TOO_MANY (5 an hour) · 429 TOO_MANY_OPEN (3 open)
 *                                         · 400 SIGN_IN_REQUIRED
 *   GET  v3/admin/tools/bot-test-card   → { sentTo, installed, problem, maxPerHour, maxOpen, openCount, items: [...] }
 *
 * The Retail Chat Bot sends ONE real approval card to the signed-in person's everyday Teams account, through the same proactive
 * path every Retail alert uses, built by the same composer as a real risk-approval card. Pressing Approve or Reject goes
 * through the bot's real settle path and the card redraws as settled; only the test records the press — no approval, ask,
 * callback, message, e-mail or task changes. While a card is open this page re-reads every 5 seconds.
 */

import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '@retail-ops/api-client';
import { EmptyState, PageHeader, StatusChip, type StatusTone } from '@retail-ops/panel-shell';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';

const ROUTE = '/v3/admin/tools/bot-test-card';
const POLL_MS = 5000;

export interface TestItem {
  testId: string;
  /** The send: sent | failed | not-installed */
  outcome: string;
  detail: string | null;
  sentTo: string;
  signedInAs: string | null;
  at: string;
  /** What happened to the card: Open | Approved | Rejected | Expired | Failed | NotInstalled (absent from a 3.41 API). */
  state?: string;
  settledBy?: string | null;
  settledAt?: string | null;
  comment?: string | null;
  /** 'teams' when the press came from the card in Teams. */
  channel?: string | null;
}

interface StatusAnswer {
  sentTo: string;
  installed: boolean;
  problem: string | null;
  maxPerHour: number;
  maxOpen?: number;
  openCount?: number;
  items: TestItem[];
}

interface SendAnswer {
  code: 'SENT' | 'FAILED';
  testId: string | null;
  sentTo: string | null;
  message: string;
  detail: string | null;
}

/** What the page shows after a send: sent, not installed, or failed with the reason. */
export interface SendResult {
  tone: 'ok' | 'not-installed' | 'failed';
  message: string;
}

/** The card's state; a row from before 3.42 has none, so it falls back to the send outcome. */
export function stateOf(i: TestItem): string {
  if (i.state) return i.state;
  return i.outcome === 'sent' ? 'Sent' : i.outcome === 'not-installed' ? 'NotInstalled' : 'Failed';
}

const STATE: Record<string, { tone: StatusTone; label: string }> = {
  Open: { tone: 'open', label: 'Sent' },
  Sent: { tone: 'ok', label: 'Sent' },
  Approved: { tone: 'ok', label: 'Approved' },
  Rejected: { tone: 'problem', label: 'Rejected' },
  Expired: { tone: 'neutral', label: 'Expired' },
  Failed: { tone: 'problem', label: 'Failed' },
  NotInstalled: { tone: 'watch', label: 'Not installed' },
};

/** Still waiting for a press in Teams. */
export function isOpen(i: TestItem): boolean {
  return stateOf(i) === 'Open';
}

/** The "How" column, in plain words. */
export function howText(i: TestItem): string {
  const s = stateOf(i);
  if (s === 'Open') return 'Waiting for your press in Teams';
  if (s === 'Approved' || s === 'Rejected') {
    const how = i.channel === 'teams' ? 'Pressed in Teams' : 'Answered';
    return i.settledBy ? `${how} by ${i.settledBy}` : how;
  }
  if (s === 'Expired') return 'Not answered within 24 hours';
  if (s === 'NotInstalled') return 'The bot has no chat with you yet';
  if (s === 'Failed') return 'The card did not reach Teams';
  return 'Sent (before the card had buttons)';
}

/** Plain words for a refused or failed send. */
export function sendResultFrom(e: unknown): SendResult {
  if (e instanceof ApiError) {
    if (e.code === 'NOT_INSTALLED') return { tone: 'not-installed', message: e.message };
    return { tone: 'failed', message: e.message };
  }
  return { tone: 'failed', message: e instanceof Error ? e.message : String(e) };
}

function fmtCt(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' })} CT`;
}

export function RetailBotTestCardPage(): JSX.Element {
  const client = useApiClient();
  const [data, setData] = useState<StatusAnswer | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<SendResult | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      setData(await client.http.get<StatusAnswer>(ROUTE));
      setLoadError(null);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : String(e));
    }
  }, [client]);

  useEffect(() => {
    void load();
  }, [load]);

  const items = data?.items ?? [];
  const anyOpen = items.some(isOpen);
  useEffect(() => {
    if (!anyOpen) return undefined;
    const t = window.setInterval(() => void load(), POLL_MS);
    return () => window.clearInterval(t);
  }, [anyOpen, load]);

  const maxOpen = data?.maxOpen ?? 3;
  const openCount = data?.openCount ?? items.filter(isOpen).length;
  const atLimit = data ? openCount >= maxOpen : false;

  const send = async (): Promise<void> => {
    setSending(true);
    setResult(null);
    try {
      const r = await client.http.post<SendAnswer>(ROUTE, { body: {} });
      setResult(r.code === 'SENT' ? { tone: 'ok', message: r.message } : { tone: 'failed', message: r.message });
    } catch (e) {
      setResult(sendResultFrom(e));
    } finally {
      setSending(false);
      await load();
    }
  };

  const columns: ReadonlyArray<DataTableColumn<TestItem>> = [
    {
      header: 'Test card',
      cell: (i) => (
        <>
          <span className="cell-strong">{i.testId}</span>
          <div className="cell-desc">Sent {fmtCt(i.at)} to {i.sentTo}</div>
        </>
      ),
      sortKey: (i) => i.at,
      cardTitle: true,
    },
    {
      header: 'Result',
      cell: (i) => {
        const o = STATE[stateOf(i)] ?? { tone: 'neutral' as StatusTone, label: stateOf(i) };
        return <StatusChip tone={o.tone}>{o.label}</StatusChip>;
      },
      sortKey: (i) => stateOf(i),
    },
    { header: 'When', cell: (i) => fmtCt(i.settledAt ?? i.at), sortKey: (i) => i.settledAt ?? i.at },
    {
      header: 'How',
      cell: (i) => (
        <>
          {howText(i)}
          {i.comment && <div className="cell-desc">&ldquo;{i.comment}&rdquo;</div>}
          {i.detail && <div className="cell-desc">{i.detail}</div>}
        </>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Send me a test Teams card"
        intro={
          <>
            Sends one real approval card from the Retail Chat Bot to your own Teams chat{data?.sentTo ? ` (${data.sentTo})` : ''}, the
            same way every Retail alert is sent. Press Approve or Reject in Teams: the press goes through the bot&apos;s real settle path
            and the card redraws as settled, but only this test records it. No approval, ask, customer or account changes, and no one
            else is told. At most {maxOpen} can be open at a time and {data?.maxPerHour ?? 5} sent an hour; an unanswered one expires
            after 24 hours.
          </>
        }
        actions={
          <Button onClick={() => void send()} disabled={!data || sending || atLimit}>
            {sending ? 'Sending…' : 'Send me a test Teams card'}
          </Button>
        }
      />

      {data && !data.installed && data.problem && (
        <Banner tone="warn">
          <strong>Not installed.</strong> {data.problem}
        </Banner>
      )}
      {atLimit && (
        <Banner tone="info">
          You have {openCount} open test cards. Press Approve or Reject on one in Teams before sending another.
        </Banner>
      )}
      {result && (
        <Banner tone={result.tone === 'ok' ? 'info' : result.tone === 'not-installed' ? 'warn' : 'err'}>
          <strong>{result.tone === 'ok' ? 'Sent.' : result.tone === 'not-installed' ? 'Not installed.' : 'Failed.'}</strong> {result.message}{' '}
          <Button variant="ghost" size="sm" onClick={() => setResult(null)}>
            Dismiss
          </Button>
        </Banner>
      )}
      {loadError && (
        <Banner tone="err">
          Could not load your test cards: {loadError}{' '}
          <Button variant="ghost" size="sm" onClick={() => void load()}>
            Retry
          </Button>
        </Banner>
      )}

      {!data && !loadError ? (
        <Spinner label="Loading your test cards…" />
      ) : (
        <Card title={anyOpen ? 'Your recent test cards — checking for your press every few seconds' : 'Your recent test cards'}>
          <DataTable<TestItem>
            columns={columns}
            rows={items}
            rowKey={(i) => `${i.testId}-${i.at}`}
            emptyState={<EmptyState title="You have not sent a test card yet.">Press the button above to send one to yourself.</EmptyState>}
          />
        </Card>
      )}
    </div>
  );
}
